export const CATEGORY_LABELS: Record<string, string> = {
  identify: 'Profile updates',
  events: 'Incoming events',
  campaigns: 'Campaign messages',
  broadcasts: 'Broadcast sends',
  transactional: 'Transactional messages',
  delivery_metrics: 'Delivery metrics',
  csv_imports: 'CSV imports',
  integrations: 'Integrations',
  objects: 'Custom objects',
  segments: 'Segment processing',
};

export const EGRESS_CATEGORY_LABELS: Record<string, string> = {
  message_delivery: 'Message delivery',
  transactional_delivery: 'Transactional delivery',
  webhooks: 'Webhooks',
  journey_actions: 'Journey actions',
  alias: 'Profile merging',
  related_updates: 'Object segment updates',
};

export const SERVICE_INFO_TOOLTIPS: Record<string, string> = {
  identify: 'Profile and identity data from your app, SDK, or API.',
  events: 'Custom events sent for segmentation and workflow triggers.',
  campaigns: 'One-time or recurring campaign messages.',
  broadcasts: 'One-time messages to segments or lists.',
  transactional: 'Triggered messages (e.g. password reset, receipts).',
  delivery_metrics:
    'Delivery metrics processing — tracks sent, opened, clicked, bounced, and complaint events. This is not message delivery itself.',
  csv_imports: 'Profiles or events imported via CSV upload.',
  integrations: 'Data synced from connected integrations.',
  objects: 'Custom object data (e.g. orders, products).',
  segments: 'Segment membership calculations and updates.',
  message_delivery: 'Campaign and broadcast delivery to email, push, SMS.',
  transactional_delivery: 'Transactional message delivery to channels.',
  webhooks: 'Outbound webhook calls to your endpoints.',
  journey_actions: 'Actions executed in journeys (sends, delays, etc.).',
  alias: 'Profile merge and alias resolution.',
  related_updates: 'Segment updates driven by custom object changes.',
};

export const CATEGORY_IMPACT: Record<string, string> = {
  identify: 'Profile updates may not appear immediately — segments and filters could show stale data.',
  events: 'Incoming events are queued — campaign triggers and behavioral workflows may fire late.',
  campaigns: 'Campaign messages are waiting to send — recipients will see delays.',
  broadcasts: 'Broadcast sends are backed up — delivery will be slower than expected.',
  transactional: 'Transactional messages (e.g. password resets, confirmations) are delayed.',
  delivery_metrics: 'Delivery metric processing is slow — open, click, and sent counts may be recorded late.',
  csv_imports: 'CSV imports are still processing — imported profiles and attributes are not yet available.',
  integrations: 'Integration imports are still processing — imported profiles are not yet available.',
  objects: 'Custom object updates are pending — relationships and object data may be outdated.',
  segments: 'Segment rebuilds are behind — membership may not reflect recent changes.',
  message_delivery: 'Emails, push notifications, and SMS may arrive late.',
  transactional_delivery: 'Transactional messages (e.g. password resets) are delayed.',
  webhooks: 'Webhook delivery is backed up — external integrations may receive events late.',
  journey_actions: 'Journey action processing is delayed — campaign messages may take longer to send.',
  alias: 'Profile merging is delayed — some duplicate profiles may appear temporarily.',
  related_updates: 'Object-related segment updates are delayed — membership may not reflect recent changes.',
};

export function formatThroughput(perSecond: number): string {
  if (perSecond <= 0) return '0/s';
  if (perSecond >= 1) return `${Math.round(perSecond).toLocaleString()}/s`;
  const perMinute = perSecond * 60;
  if (perMinute >= 1) return `${Math.round(perMinute)}/min`;
  return '<1/min';
}

export function processingStatusLabel(rawDrainSeconds: number | null | undefined): {
  label: string;
  color: 'green' | 'yellow' | 'red';
} {
  const drainSeconds = rawDrainSeconds != null ? Math.round(rawDrainSeconds) : null;
  if (drainSeconds == null || drainSeconds < 180) {
    return { label: 'Current', color: 'green' };
  }
  if (drainSeconds < 600) {
    return { label: 'A few minutes behind', color: 'yellow' };
  }
  if (drainSeconds < 3600) {
    const mins = Math.round(drainSeconds / 60);
    return { label: `About ${mins} minutes behind`, color: 'red' };
  }
  const totalMins = Math.round(drainSeconds / 60);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const duration = mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
  return { label: `About ${duration} behind`, color: 'red' };
}

export function formatCompactDuration(seconds: number): string {
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.round(rounded / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h ${remainingMins}min`;
}

export const HIGH_IMPACT_CATEGORIES = new Set([
  'identify',
  'events',
  'campaigns',
  'transactional',
  'segments',
  'message_delivery',
  'transactional_delivery',
  'journey_actions',
]);

export const SEVERITY_COLOR: Record<string, 'yellow' | 'red'> = {
  caution: 'yellow',
  error: 'red',
};

export function delayStatus(
  delaySeconds: number | null | undefined,
  pending: number,
): { label: string; color: 'green' | 'yellow' | 'red' } {
  if (pending === 0) return { label: 'Clear', color: 'green' };
  if (delaySeconds == null || delaySeconds < 10) return { label: 'Current', color: 'green' };
  if (delaySeconds < 60) return { label: 'Slight delay', color: 'yellow' };
  if (delaySeconds < 300) return { label: 'Behind', color: 'yellow' };
  return { label: 'Backed up', color: 'red' };
}
